
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import UserAvatar from '../common/UserAvatar';
import Loading from '../common/Loading';
import './styles/Profile.css';

const extractAllImages = (user) => {
  const urls = [];

  // 1) photos array (backend structure)
  if (Array.isArray(user?.photos)) {
    urls.push(...user.photos.map(p => p?.image_url).filter(Boolean));
  }

  // 2) images array (already processed in Dashboard)
  if (Array.isArray(user?.images)) {
    urls.push(...user.images);
  }

  // 3) single image fields
  const single = user?.profile_image || user?.profile_picture || user?.image;
  if (single) urls.push(single);

  // Deduplicate
  const unique = Array.from(new Set(urls.filter(Boolean)));

  if (unique.length === 0) {
    // Fallback gradient
    return [{
      id: 'fallback',
      color: 'linear-gradient(135deg, #003A8F, #60a5fa)',
      isGradient: true
    }];
  }

  return unique.map(url => ({ url, id: url }));
};

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Try to use passed state first
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!user);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);

  useEffect(() => {
    if (user) {
      setImages(extractAllImages(user));
      setLoading(false);
      return;
    }

    const loadUserProfile = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/users/${userId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error('Profile not found');
        const data = await res.json();
        setUser(data);
        setImages(extractAllImages(data));
      } catch (err) {
        console.error('Error loading user profile:', err);
        setError('Profile not found');
        setUser(null);
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [userId, user]);

  const handleStartConversation = async () => {
    if (!user) return;
    navigate('/chat/new', { state: { user } });
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="profile-page">
        <Loading message="Loading profile..." />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="profile-page">
        <div className="profile-error">
          <h2>Profile not found</h2>
          <p>The user profile could not be loaded.</p>
          <button className="back-btn" onClick={handleBackClick}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page user-profile">
      {/* HEADER */}
      <div className="profile-header">
        <button className="back-btn" onClick={handleBackClick}>
          ← Back
        </button>

        <div className="header-left">
          <div className="profile-avatar-large">
            <UserAvatar user={user} size={80} />
          </div>

          <div className="profile-info">
            <h1>
              {user.name}
              {user.age ? `, ${user.age}` : ''}
            </h1>
            <p>
              {user.job || '—'} · {user.location || '—'}
            </p>
            <p className="member-since">
              Member since {user.memberSince || 'Recently'}
            </p>
          </div>
        </div>

        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleStartConversation}>
            <i className="fas fa-comment"></i> Start Chat
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => alert('Like functionality goes here')}
          >
            <i className="fas fa-heart"></i> Like
          </button>
        </div>
      </div>

      {/* CONTENT */}
      <div className="profile-tabs">
        <div className="tabs-navigation">
          <button className="tab-btn active">
            <i className="fas fa-user"></i> Profile
          </button>
        </div>

        <div className="tabs-content">
          <div className="profile-tab-content">
            {/* BIO */}
            <div className="profile-bio-section">
              <h3>About Me</h3>
              <p className="bio-text">
                {user.bio || "This user hasn't added a bio yet."}
              </p>
            </div>

            {/* DETAILS */}
            <div className="profile-details-section">
              <h3>Details</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Age</span>
                  <span className="detail-value">{user.age || '—'}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Gender</span>
                  <span className="detail-value">{user.gender || '—'}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Location</span>
                  <span className="detail-value">{user.location || '—'}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Job</span>
                  <span className="detail-value">{user.job || '—'}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Education</span>
                  <span className="detail-value">
                    {user.education || 'Not specified'}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Relationship Goal</span>
                  <span className="detail-value">
                    {user.relationshipGoal || '—'}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Personality</span>
                  <span className="detail-value">
                    {user.personality || '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* INTERESTS */}
            {user.interests && user.interests.length > 0 && (
              <div className="profile-interests-section">
                <h3>Interests</h3>
                <div className="interests-grid">
                  {user.interests.map((interest, index) => (
                    <div key={index} className="interest-item">
                      {interest}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PHOTOS - FIXED VERSION */}
            {images.length > 0 && (
              <div className="profile-photos-section">
                <h3>Photos</h3>
                <div className="photos-grid">
                  {images.map((img, index) => (
                    <div key={img.id || index} className="photo-item">
                      <div
                        className="photo-preview"
                        style={
                          img.isGradient
                            ? { background: img.color }
                            : {
                                backgroundImage: `url(${img.url || img})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                              }
                        }
                      >
                        {index === 0 && (
                          <div className="primary-badge">
                            <i className="fas fa-star"></i> Primary
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;